import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import { NumberStepper } from '../components/NumberStepper';
import { getRecommendations } from '../lib/api';
import { useKiosk } from '../lib/session';

const DURATIONS: { labelKey: string; minutes: number }[] = [
  { labelKey: 'recommend.time30', minutes: 30 },
  { labelKey: 'recommend.time60', minutes: 60 },
  { labelKey: 'recommend.time120', minutes: 120 },
  { labelKey: 'recommend.halfDay', minutes: 240 },
];

/** 體驗項目對映 products.category（設計稿：搭船遊港／水上體驗／套裝／包船）。 */
const CATEGORIES = ['trip', 'experience', 'package', 'charter'] as const;

/** 可選的出發日數。設計稿畫三天。 */
const DAY_CHOICES = 3;

/** 本地日期鍵 `YYYY-MM-DD`；用 UTC 會讓晚上的「今天」變成明天。 */
function dayKey(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function upcomingDays(n: number): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < n; i += 1) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}

/**
 * 行程推薦（問卷）— 設計稿 2026-08-19 第 7 頁第 1 步。
 *
 * 四個提問：日期 · 時長 · 人數 · 體驗項目（可複選）。答完按「請小獅船長推薦」
 * 進到專屬行程頁。
 *
 * ## 日期是真的會生效的條件
 *
 * 不是只擺在畫面上：它會送到後端，只推薦**當天真的有位子**的商品（未停航、
 * 剩餘座位夠這一團）。推薦一個當天沒航班的行程，等顧客按下訂購才發現沒得選，
 * 是推薦最不該做的事。
 *
 * ## 兒童年齡區間照設計稿留問號
 *
 * 設計稿寫「兒童(?~?歲)」並標為待確認。自行填一個數字會變成機台上對旅客的
 * 承諾，票價與年齡的對應也還沒定。
 */
export function RecommendScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, patchDraft, draft } = useKiosk();

  const days = upcomingDays(DAY_CHOICES);
  const [date, setDate] = useState(() => dayKey(days[0] as Date));
  const [stayMinutes, setStayMinutes] = useState(120);
  const [adults, setAdults] = useState(draft.quantities.adult ?? 2);
  const [children, setChildren] = useState(draft.quantities.child ?? 0);
  const [interests, setInterests] = useState<string[]>([]);

  const ask = useMutation({
    mutationFn: () =>
      getRecommendations({
        stay_minutes: stayMinutes,
        party_adults: adults,
        party_children: children,
        date,
        ...(interests.length > 0 ? { interests } : {}),
      }),
    onSuccess: (data) => {
      // 條件與結果一起放進 draft：結果頁與後續的班次頁都要用到同一組
      patchDraft({
        recommendDate: date,
        recommendations: data.recommendations,
        quantities: { adult: adults, ...(children > 0 ? { child: children } : {}) },
      });
      navigate('/recommend/result');
    },
  });

  function toggleInterest(cat: string): void {
    setInterests((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  }

  const tag = locale === 'en' ? 'en-US' : locale === 'ja' ? 'ja-JP' : 'zh-TW';
  const dateLabel = (d: Date): string =>
    d.toLocaleDateString(tag, { month: '2-digit', day: '2-digit', weekday: 'short' });

  return (
    <KioskFrame bleed>
      <HeroBanner height={340}>
        <Mascot size={190} pose="smile" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {t('menu.recommend')}
      </h1>
      <p className="k-subtitle">{t('recommend.lead')}</p>

      <Question label={t('recommend.dateQ')}>
        <div className="k-choices">
          {days.map((d) => {
            const key = dayKey(d);
            return (
              <button
                key={key}
                type="button"
                className={`k-choice${date === key ? ' k-choice--active' : ''}`}
                aria-pressed={date === key}
                onClick={() => setDate(key)}
              >
                {dateLabel(d)}
              </button>
            );
          })}
        </div>
      </Question>

      <Question label={t('recommend.durationQ')}>
        <div className="k-choices">
          {DURATIONS.map((o) => (
            <button
              key={o.minutes}
              type="button"
              className={`k-choice${stayMinutes === o.minutes ? ' k-choice--active' : ''}`}
              aria-pressed={stayMinutes === o.minutes}
              onClick={() => setStayMinutes(o.minutes)}
            >
              {t(o.labelKey)}
            </button>
          ))}
        </div>
      </Question>

      <Question label={t('recommend.partySizeQ')}>
        <div className="k-party">
          <NumberStepper
            label={t('schedule.adults')}
            value={adults}
            min={1}
            max={20}
            onChange={setAdults}
          />
          <NumberStepper
            label={t('recommend.childRange')}
            value={children}
            min={0}
            max={20}
            onChange={setChildren}
          />
        </div>
      </Question>

      <Question label={t('recommend.experienceQ')}>
        <div className="k-choices">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`k-choice${interests.includes(c) ? ' k-choice--active' : ''}`}
              aria-pressed={interests.includes(c)}
              onClick={() => toggleInterest(c)}
            >
              {t(`cat.${c}`)}
            </button>
          ))}
        </div>
      </Question>

      {ask.isError && (
        <p className="k-error" style={{ textAlign: 'center' }}>
          {t('common.errGeneric')}
        </p>
      )}

      <div className="k-footbar" style={{ justifyContent: 'center', gap: 28, marginTop: 8 }}>
        <KioskButton variant="secondary" onClick={() => navigate('/home')}>
          ← {t('common.back')}
        </KioskButton>
        <KioskButton variant="primary" large disabled={ask.isPending} onClick={() => ask.mutate()}>
          {ask.isPending ? t('common.loading') : `${t('recommend.ask')} →`}
        </KioskButton>
      </div>
    </KioskFrame>
  );
}

/** 一個提問區塊：菱形標記 + 標題 + 選項，區塊之間以細線分隔（設計稿）。 */
function Question({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="k-question">
      <h2 className="k-question__title">
        <span className="k-question__mark" aria-hidden="true" />
        {label}
      </h2>
      {children}
    </section>
  );
}
